import type { KeyLevelsModel } from './KeyLevelsModel/KeyLevelsModel';
import { createDefaultActiveCategories } from './KeyLevelsModel/state/types';
import { ChronoUnit, LocalDate } from '@js-joda/core';
import { computed, createModel, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
// `Period` must come from common's re-export, not `@js-joda/core` directly:
// the wire codec checks `instanceof Period` and Periods constructed via a
// different js-joda module instance fail equality / instanceof tests.
import { Period } from '@thinkalpha/common/schemas/codecs.js';
import { AlphaEyeWidgetContent } from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';
import type {
    AlphaEyeDataListFontSize,
    AlphaEyeKeyLevelCategory,
    AlphaEyeMainSectionId,
    AlphaEyeNewsSubTabId,
    AlphaEyePageId,
} from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import type { TableStatus } from 'src/components/table-view/model';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { getUnderlyingSymbolFromInstrument, parseInstrument } from 'src/lib/instrumentUtils';
import type { AnalystRatingsModel } from 'src/models/AnalystRatingsModel';
import { LinkingMailboxModel } from 'src/models/LinkingMailboxModel';
import type { TableModel } from 'src/models/TableModel';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import { ContainerChannelSlice } from 'src/services/WorkspaceService/Container/slices/ContainerChannelSlice';
import { ContainerWidgetTabSlice } from 'src/services/WorkspaceService/Container/slices/ContainerWidgetTabSlice';
import type { LinkEvent } from 'src/services/WorkspaceService/linking';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

const ALLOWED_INSTRUMENT_TYPES: InstrumentType[] = ['equity'];
const ALPHA_EYE_ACCEPTED_INSTRUMENT_LINK_SOURCES = new Set<WidgetType>([
    'results',
    'watchlist',
    'order-blotter',
    'positions',
    'executions',
    'orders',
    'locates',
    'lab',
    'alpha-lens',
    'alpha-eye',
    'searchalpha',
    'scanner',
    'screener',
    'news',
    'events',
    'filings',
    'tradingview-chart',
]);

// Re-export the wire contract enum so consumers keep using the
// `AlphaEyeNewsSubTabId` import path that this module previously owned.
export { AlphaEyeNewsSubTabId } from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';

/**
 * Flatten a Joda `Period` (which can mix years / months / days) into a
 * calendar-day count for `LightweightChartModel.setHistoryLookbackDays`.
 * Computed against today's date so `P1Y` correctly yields 365 or 366
 * depending on the calendar year.
 */
export function lookbackToDays(period: Period): number {
    const today = LocalDate.now();
    return ChronoUnit.DAYS.between(today, today.plus(period));
}

/**
 * Persisted widget-instance state. Aliased to the wire contract
 * `AlphaEyeWidgetContent` so the local model and the workspace PUT payload
 * stay in lockstep — this widget is server-persisted (no longer
 * `isLocalOnly`) and the workspace `Widget` discriminated union expects
 * exactly this shape under the `'alpha-eye'` discriminator. `chartLookback`
 * is a `Period` after schema decode; on the wire it travels as an ISO-8601
 * string (`P3M` / `P6M` / `P1Y`).
 */
export type AlphaEyeWidgetState = z.output<typeof AlphaEyeWidgetContent>;

/**
 * Duck-typed encoder: detect any `Period`-shaped object (regardless of which
 * js-joda module instance constructed it) and emit its ISO-8601 string. In
 * dev with `link:`-resolved platform-ws-client, the linked client carries a
 * separate `@thinkalpha/common` (and transitively a separate `@js-joda/core`)
 * from the one the UI imports — so `v instanceof Period` from this module's
 * Period class would fail for Periods returned by the linked-client schema.
 * `toJSON()` is on every Period's prototype and returns the canonical
 * `P…` string, which is exactly the codec's input shape.
 */
function isPeriodLike(v: unknown): v is { toJSON(): string } {
    return (
        typeof v === 'object' &&
        v !== null &&
        typeof (v as { toJSON?: unknown }).toJSON === 'function' &&
        typeof (v as { years?: unknown }).years === 'function' &&
        typeof (v as { months?: unknown }).months === 'function' &&
        typeof (v as { days?: unknown }).days === 'function'
    );
}

/**
 * Normalize a `Period` from any js-joda instance into this module's local
 * `Period` so equality / `LocalDate.plus(...)` calls in `lookbackToDays`
 * stay within a single module's class identity.
 */
function normalizeToLocalPeriod(p: { years(): number; months(): number; days(): number }): Period {
    return Period.of(p.years(), p.months(), p.days());
}

/**
 * Parsing schema. `widget.toJSON()` returns a live `Period` instance for
 * `chartLookback` (matching the in-memory state type), but `Container/impl.ts`
 * re-parses that output against this schema as a defensive validation step.
 * The bare `PeriodCodec` only accepts ISO strings — pre-encoding any
 * `Period`-shaped input back to its string form here lets the same schema
 * accept both fresh-from-the-wire strings *and* in-memory Period instances.
 * After decode, the transform normalizes the result to this module's
 * `Period` class so downstream `LocalDate.plus(...)` and `.equals(...)`
 * work reliably regardless of which js-joda instance produced it.
 */
const ChartLookbackTolerant = z.preprocess(
    (v) => (isPeriodLike(v) ? v.toJSON() : v),
    AlphaEyeWidgetContent.shape.chartLookback,
);

const AlphaEyeInitializedWidgetContent = AlphaEyeWidgetContent.extend({
    symbol: AlphaEyeWidgetContent.shape.symbol.refine((symbol) => symbol.trim().length > 0, {
        message: 'AlphaEye initialized state requires a symbol',
    }),
});

export const alphaEyeWidgetStateSchema = AlphaEyeInitializedWidgetContent.extend({
    chartLookback: ChartLookbackTolerant,
}).transform(
    (s): AlphaEyeWidgetState => ({
        ...s,
        chartLookback: s.chartLookback ? normalizeToLocalPeriod(s.chartLookback) : undefined,
    }),
) satisfies z.ZodType<AlphaEyeWidgetState>;

export type AlphaEyeWidgetInitializingState = Omit<Partial<AlphaEyeWidgetState>, 'symbol'> & {
    initialization: 'default-symbol';
    symbol?: never;
};

export type AlphaEyeWidgetInitialState = AlphaEyeWidgetState | AlphaEyeWidgetInitializingState;

export type AlphaEyeWidgetRuntimeState =
    | { type: 'uninitialized' }
    | { type: 'initializing' }
    | { type: 'ready' }
    | { type: 'error'; error: Error };

const alphaEyeWidgetInitializingStateSchema = AlphaEyeWidgetContent.omit({ symbol: true })
    .extend({
        initialization: z.literal('default-symbol'),
        chartLookback: ChartLookbackTolerant,
    })
    .transform(
        (s): AlphaEyeWidgetInitializingState => ({
            ...s,
            chartLookback: s.chartLookback ? normalizeToLocalPeriod(s.chartLookback) : undefined,
        }),
    );

export const alphaEyeWidgetInitialStateSchema = z.union([
    alphaEyeWidgetStateSchema,
    alphaEyeWidgetInitializingStateSchema,
]) satisfies z.ZodType<AlphaEyeWidgetInitialState>;

const DEFAULT_CURRENT_PAGE_ID: AlphaEyePageId = 'main';
const DEFAULT_ACTIVE_MAIN_SUB_TAB_ID: AlphaEyeMainSectionId = 'general';
const DEFAULT_ACTIVE_NEWS_SUB_TAB_ID: AlphaEyeNewsSubTabId = 'news';
const DEFAULT_DATA_LIST_FONT_SIZE: AlphaEyeDataListFontSize = 'small';
const DEFAULT_IS_CHART_COLLAPSED = false;
const DEFAULT_CHART_LOOKBACK: Period = Period.ofMonths(3);

type AlphaEyeModelInitBase = {
    persisted: AlphaEyeWidgetInitialState;
    logger: Logger;
    loadDefaultSymbol: () => Promise<string>;
    createActiveTabModel: CreateAlphaEyeActiveTabModel;
};

type AlphaEyeWorkspaceModelInit = AlphaEyeModelInitBase & {
    mode: 'workspace';
    container: WidgetHostContainer;
    tabId: WidgetId;
    workspaceService: Pick<WorkspaceService, 'publishLinkIntent' | 'subscribeLinkEvents'>;
};

type AlphaEyeStandaloneModelInit = AlphaEyeModelInitBase & {
    mode: 'standalone';
};

type AlphaEyeModelInit = AlphaEyeWorkspaceModelInit | AlphaEyeStandaloneModelInit;

/**
 * Value shape of the model's active-sub-tab connection-status signal. Mirrors
 * the eventBus `widget::connectionStatus` signal shape used by every other
 * widget; consumed by the reusable `ConnectionStatus.svelte`.
 */
export interface MainSubTabConnectionStatusValue {
    status: TableStatus;
    progress: number | undefined;
    failureCount: number;
}

export type AlphaEyePassiveActiveTabModel = Disposable & {
    readonly pageId: Exclude<AlphaEyePageId, 'analyst-recommendations' | 'key-levels'>;
};

export type AlphaEyeAnalystRecommendationsActiveTabModel = Disposable & {
    readonly pageId: 'analyst-recommendations';
    readonly analystRatingsModel: AnalystRatingsModel;
};

export type AlphaEyeKeyLevelsActiveTabModel = Disposable & {
    readonly pageId: 'key-levels';
    readonly keyLevelsModel: KeyLevelsModel;
    readonly keyLevelsStatsTable: TableModel;
};

export type AlphaEyeActiveTabModel =
    | AlphaEyePassiveActiveTabModel
    | AlphaEyeAnalystRecommendationsActiveTabModel
    | AlphaEyeKeyLevelsActiveTabModel;

export type AlphaEyeActiveTabModelState =
    | { readonly status: 'uninitialized' }
    | { readonly status: 'initializing'; readonly pageId: AlphaEyePageId }
    | { readonly status: 'initialized'; readonly pageId: AlphaEyePageId; readonly model: AlphaEyeActiveTabModel };

export type CreateAlphaEyeActiveTabModel = (pageId: AlphaEyePageId) => AlphaEyeActiveTabModel;

function isInitializedAlphaEyeWidgetState(state: AlphaEyeWidgetInitialState): state is AlphaEyeWidgetState {
    return 'symbol' in state && typeof state.symbol === 'string' && state.symbol.trim().length > 0;
}

function initializationError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
}

export const AlphaEyeModel = createModel((init: AlphaEyeModelInit) => {
    const initialState = init.persisted;
    const hasInitializedState = isInitializedAlphaEyeWidgetState(initialState);
    const runtimeState = signal<AlphaEyeWidgetRuntimeState>({ type: 'uninitialized' });
    const symbol = signal(hasInitializedState ? initialState.symbol : '');
    const isChartCollapsed = signal(initialState.isChartCollapsed ?? DEFAULT_IS_CHART_COLLAPSED);
    const currentPageId = signal<AlphaEyePageId>(initialState.currentPageId ?? DEFAULT_CURRENT_PAGE_ID);
    const activeMainSubTabId = signal<AlphaEyeMainSectionId>(
        initialState.activeMainSubTabId ?? DEFAULT_ACTIVE_MAIN_SUB_TAB_ID,
    );
    const activeNewsSubTabId = signal<AlphaEyeNewsSubTabId>(
        initialState.activeNewsSubTabId ?? DEFAULT_ACTIVE_NEWS_SUB_TAB_ID,
    );
    const dataListFontSize = signal<AlphaEyeDataListFontSize>(
        initialState.dataListFontSize ?? DEFAULT_DATA_LIST_FONT_SIZE,
    );
    const goToDate = signal<string | null>(initialState.goToDate ?? null);
    const displayMonth = signal<string | null>(initialState.displayMonth ?? null);
    const chartLookback = signal<Period>(initialState.chartLookback ?? DEFAULT_CHART_LOOKBACK);
    const persistedActiveCategories = initialState.keyLevels?.activeCategories;
    const keyLevelsActiveCategories = signal<ReadonlySet<AlphaEyeKeyLevelCategory> | null>(
        persistedActiveCategories === undefined
            ? createDefaultActiveCategories()
            : persistedActiveCategories === null
              ? null
              : new Set(persistedActiveCategories),
    );
    const activeTabModelBox = new DisposableBox<AlphaEyeActiveTabModel>();
    const activeTabModel = signal<AlphaEyeActiveTabModelState>({ status: 'uninitialized' });

    // Registry of per-sub-tab TableModels — pushed in by MainSubTab.svelte
    // when it mounts each sub-tab table. Driven through a Map signal so the
    // connection-status computed below can react to registration changes.
    const mainSubTabTables = signal(new Map<AlphaEyeMainSectionId, TableModel>());

    const dirty = signal(false);
    const allowedInstrumentTypes = signal<InstrumentType[]>(ALLOWED_INSTRUMENT_TYPES);

    const symbolReadonly: ReadonlySignal<string> = computed(() => symbol.value);
    const runtimeStateReadonly: ReadonlySignal<AlphaEyeWidgetRuntimeState> = computed(() => runtimeState.value);
    const isChartCollapsedReadonly: ReadonlySignal<boolean> = computed(() => isChartCollapsed.value);
    const currentPageIdReadonly: ReadonlySignal<AlphaEyePageId> = computed(() => currentPageId.value);
    const activeMainSubTabIdReadonly: ReadonlySignal<AlphaEyeMainSectionId> = computed(() => activeMainSubTabId.value);
    const activeNewsSubTabIdReadonly: ReadonlySignal<AlphaEyeNewsSubTabId> = computed(() => activeNewsSubTabId.value);
    const dataListFontSizeReadonly: ReadonlySignal<AlphaEyeDataListFontSize> = computed(() => dataListFontSize.value);
    const goToDateReadonly: ReadonlySignal<string | null> = computed(() => goToDate.value);
    const displayMonthReadonly: ReadonlySignal<string | null> = computed(() => displayMonth.value);
    const chartLookbackReadonly: ReadonlySignal<Period> = computed(() => chartLookback.value);
    const keyLevelsActiveCategoriesReadonly: ReadonlySignal<ReadonlySet<AlphaEyeKeyLevelCategory> | null> = computed(
        () => keyLevelsActiveCategories.value,
    );
    const activeTabModelReadonly: ReadonlySignal<AlphaEyeActiveTabModelState> = computed(() => activeTabModel.value);
    const dirtyReadonly: ReadonlySignal<boolean> = computed(() => dirty.value);
    const allowedInstrumentTypesReadonly: ReadonlySignal<InstrumentType[]> = computed(
        () => allowedInstrumentTypes.value,
    );

    effect(() => () => {
        activeTabModelBox[Symbol.dispose]();
    });

    effect(() => {
        const pageId = currentPageId.value;
        let cancelled = false;
        activeTabModel.value = { status: 'initializing', pageId };
        const handle = setTimeout(() => {
            if (cancelled) return;
            const model = init.createActiveTabModel(pageId);
            if (cancelled) {
                model[Symbol.dispose]();
                return;
            }
            activeTabModelBox.next(model);
            activeTabModel.value = { status: 'initialized', pageId, model };
        }, 0);

        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    });

    // Stable preact-computed signal for the active sub-tab's full connection
    // state. Identity is fixed for the lifetime of the model; Svelte's
    // auto-store `$prefix` consumes the value reactively without an
    // `$effect` bridge. Shape matches the standard `widget::connectionStatus`
    // eventBus signal used by every other widget so a single `ConnectionStatus`
    // component can render it everywhere.
    const mainSubTabConnectionStatus: ReadonlySignal<MainSubTabConnectionStatusValue> = computed(() => {
        const id = activeMainSubTabId.value;
        const t = mainSubTabTables.value.get(id);
        return {
            status: t?.connectionStatus.value ?? 'uninitialized',
            progress: t?.progress.value,
            failureCount: t?.failureCount.value ?? 0,
        };
    });

    const mainSubTabTableKey: ReadonlySignal<string> = computed(() => {
        const id = activeMainSubTabId.value;
        const t = mainSubTabTables.value.get(id);
        return t?.keySignal.value ?? '';
    });

    const setSymbolValue = (next: string, { markDirty = true }: { markDirty?: boolean } = {}): boolean => {
        if (symbol.value === next) {
            if (runtimeState.value.type !== 'ready') runtimeState.value = { type: 'ready' };
            return false;
        }
        symbol.value = next;
        runtimeState.value = { type: 'ready' };
        if (markDirty) dirty.value = true;
        return true;
    };

    const normalizeSymbol = (instrument: string): string | null => {
        try {
            return getUnderlyingSymbolFromInstrument(parseInstrument(instrument));
        } catch (error) {
            init.logger.error('Failed to normalize AlphaEye instrument', {
                error,
                instrument,
                ...(init.mode === 'workspace' ? { tabId: init.tabId } : { mode: init.mode }),
            });
            return null;
        }
    };

    const setSymbolFromLink = (instrument: string): void => {
        const linkedSymbol = normalizeSymbol(instrument);
        if (!linkedSymbol) {
            return;
        }
        setSymbolValue(linkedSymbol);
    };

    const publishSymbolSelection = (instrument: string): void => {
        if (init.mode !== 'workspace') return;
        void init.workspaceService
            .publishLinkIntent({
                type: 'instrument-selected',
                fromTabId: init.tabId,
                details: { instrument },
            })
            .catch((error: unknown) => {
                init.logger.error('Failed to publish AlphaEye link intent', {
                    error,
                    instrument,
                    tabId: init.tabId,
                });
            });
    };

    const setSymbolFromLocalChange = (next: string): void => {
        const normalizedSymbol = normalizeSymbol(next);
        if (!normalizedSymbol || !setSymbolValue(normalizedSymbol)) return;
        publishSymbolSelection(normalizedSymbol);
    };

    const initializeDefaultSymbol = (): void => {
        if (runtimeState.value.type === 'ready') return;
        runtimeState.value = { type: 'initializing' };
        void init
            .loadDefaultSymbol()
            .then((next) => {
                if (runtimeState.value.type !== 'initializing') return;
                const normalizedSymbol = normalizeSymbol(next);
                if (!normalizedSymbol) {
                    throw new Error('AlphaEye default symbol is invalid');
                }
                setSymbolValue(normalizedSymbol);
            })
            .catch((error: unknown) => {
                if (runtimeState.value.type !== 'initializing') return;
                const normalizedError = initializationError(error);
                init.logger.error('Failed to initialize AlphaEye default symbol', {
                    error: normalizedError,
                    ...(init.mode === 'workspace' ? { tabId: init.tabId } : { mode: init.mode }),
                });
                runtimeState.value = { type: 'error', error: normalizedError };
            });
    };

    if (hasInitializedState) {
        runtimeState.value = { type: 'ready' };
    } else {
        initializeDefaultSymbol();
    }

    const ownEventBus = EventBusBuilder.create()
        .addSignal('widget::instrument', () => symbolReadonly)
        .addSignal('widget::allowedInstrumentTypes', () => allowedInstrumentTypesReadonly)
        .addEvent('widget::instrumentChange', ({ instrument }) => {
            setSymbolFromLocalChange(instrument);
        })
        .build();

    const eventBus =
        init.mode === 'workspace'
            ? (() => {
                  const channelSlice = new ContainerChannelSlice({ container: init.container, tabId: init.tabId });
                  const widgetTabSlice = new ContainerWidgetTabSlice({
                      container: init.container,
                      tabId: init.tabId,
                      logger: init.logger,
                  });

                  const acceptsLinkEvent = (event: LinkEvent): boolean => {
                      const channel = channelSlice.channel.value;
                      return (
                          channel !== null &&
                          event.channelId === channel &&
                          event.type === 'instrument-selected' &&
                          event.from.kind === 'widget' &&
                          event.from.tabId !== init.tabId &&
                          ALPHA_EYE_ACCEPTED_INSTRUMENT_LINK_SOURCES.has(event.from.widgetType)
                      );
                  };
                  const linkingMailbox = new LinkingMailboxModel({
                      workspaceService: init.workspaceService,
                      acceptsLinkEvent,
                  });

                  effect(() => () => {
                      linkingMailbox.dispose();
                  });

                  effect(() => {
                      const linkedInstrument = linkingMailbox.instrument.value;
                      if (!linkedInstrument) return;
                      setSymbolFromLink(linkedInstrument.value);
                  });

                  return MergedEventBus.from(ownEventBus).merge(channelSlice.eventBus).merge(widgetTabSlice.eventBus);
              })()
            : ownEventBus;

    return {
        state: runtimeStateReadonly,
        symbol: symbolReadonly,
        isChartCollapsed: isChartCollapsedReadonly,
        currentPageId: currentPageIdReadonly,
        activeMainSubTabId: activeMainSubTabIdReadonly,
        activeNewsSubTabId: activeNewsSubTabIdReadonly,
        dataListFontSize: dataListFontSizeReadonly,
        goToDate: goToDateReadonly,
        displayMonth: displayMonthReadonly,
        chartLookback: chartLookbackReadonly,
        keyLevelsActiveCategories: keyLevelsActiveCategoriesReadonly,
        activeTabModel: activeTabModelReadonly,
        dirty: dirtyReadonly,
        mainSubTabConnectionStatus,
        mainSubTabTableKey,
        eventBus,
        setSymbol(next: string): void {
            setSymbolFromLocalChange(next);
        },
        setIsChartCollapsed(next: boolean): void {
            if (isChartCollapsed.value === next) return;
            isChartCollapsed.value = next;
            dirty.value = true;
        },
        setCurrentPageId(next: AlphaEyePageId): void {
            if (currentPageId.value === next) return;
            currentPageId.value = next;
            dirty.value = true;
        },
        setActiveMainSubTabId(next: AlphaEyeMainSectionId): void {
            if (activeMainSubTabId.value === next) return;
            activeMainSubTabId.value = next;
            dirty.value = true;
        },
        setActiveNewsSubTabId(next: AlphaEyeNewsSubTabId): void {
            if (activeNewsSubTabId.value === next) return;
            activeNewsSubTabId.value = next;
            dirty.value = true;
        },
        setDataListFontSize(next: AlphaEyeDataListFontSize): void {
            if (dataListFontSize.value === next) return;
            dataListFontSize.value = next;
            dirty.value = true;
        },
        setGoToDate(next: string | null): void {
            if (goToDate.value === next) return;
            goToDate.value = next;
            dirty.value = true;
        },
        setDisplayMonth(next: string | null): void {
            if (displayMonth.value === next) return;
            displayMonth.value = next;
            dirty.value = true;
        },
        setChartLookback(next: Period): void {
            // Period equality is value-based — two distinct instances of
            // `Period.ofMonths(3)` are not `===`, so we must compare via
            // `.equals()` to preserve the "no-op no dirty flip" guarantee.
            if (chartLookback.value.equals(next)) return;
            chartLookback.value = next;
            dirty.value = true;
        },
        setKeyLevelsActiveCategories(next: ReadonlySet<AlphaEyeKeyLevelCategory> | null): void {
            if (keyLevelsActiveCategories.value === next) return;
            keyLevelsActiveCategories.value = next;
            dirty.value = true;
        },
        registerMainSubTabTable(id: AlphaEyeMainSectionId, table: TableModel | null): void {
            const next = new Map(mainSubTabTables.value);
            if (table) next.set(id, table);
            else next.delete(id);
            mainSubTabTables.value = next;
        },
        refetchActiveMainSubTabTable(): void {
            const id = activeMainSubTabId.value;
            const t = mainSubTabTables.value.get(id);
            t?.refetch();
        },
        toJSON(): AlphaEyeWidgetState {
            if (runtimeState.value.type !== 'ready') {
                throw new Error(`AlphaEye widget cannot serialize while ${runtimeState.value.type}`);
            }
            return {
                symbol: symbol.value,
                isChartCollapsed: isChartCollapsed.value,
                currentPageId: currentPageId.value,
                activeMainSubTabId: activeMainSubTabId.value,
                activeNewsSubTabId: activeNewsSubTabId.value,
                dataListFontSize: dataListFontSize.value,
                goToDate: goToDate.value,
                displayMonth: displayMonth.value,
                chartLookback: chartLookback.value,
            };
        },
        markClean(): void {
            dirty.value = false;
        },
        // Lets the parent widget model flip dirty=true in response to
        // state changes it doesn't own directly — e.g. KeyLevels
        // activeCategories, which lives on KeyLevelsModel but is part of
        // the persisted AlphaEye state.
        markDirty(): void {
            if (dirty.value) return;
            dirty.value = true;
        },
    };
});

export type AlphaEyeModel = InstanceType<typeof AlphaEyeModel>;
